import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { useSignLoanAttestationMutation } from '@/apis/extended/loanApplicationApi';
import {
  isLoanAttestationSupersededByEdits,
  loanAttestationIsCurrent,
  loanAttestationNeedsRecording,
} from '@/utils/loanApplicationAttestation';
import type { LoanApplication } from '@/types/LoanApplicationType';

type Props = {
  application: LoanApplication;
  readOnly?: boolean;
};

const TERMS_VERSION = '1.0';

export function SignSection({ application, readOnly }: Props) {
  const [sign, { isLoading }] = useSignLoanAttestationMutation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (readOnly) setDialogOpen(false);
  }, [readOnly]);

  const current = loanAttestationIsCurrent(application);
  const superseded = isLoanAttestationSupersededByEdits(application);
  const needsRecording = loanAttestationNeedsRecording(application);

  const openFlow = () => {
    if (readOnly) return;
    setConfirmed(false);
    setDialogOpen(true);
  };

  const closeFlow = () => {
    setDialogOpen(false);
    setConfirmed(false);
  };

  const onRecordAttestation = async () => {
    if (readOnly || !confirmed) return;
    try {
      await sign({
        id: application._id,
        acceptedTermsVersion: TERMS_VERSION,
      }).unwrap();
      toast.success('Application attestation recorded');
      closeFlow();
    } catch {
      toast.error('Could not record attestation');
    }
  };

  return (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Electronic attestation: you confirm the information is accurate to the
        best of your knowledge. This is stored with your account and timestamp;
        no third-party e-sign vendor. Saving changes to the application after
        you attest will require a new attestation (nothing is locked—you can
        keep editing freely).
      </Typography>

      {current && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Attestation on file —{' '}
          {application.signAttestation?.acceptedAt
            ? new Date(application.signAttestation.acceptedAt).toLocaleString()
            : '—'}
          {' — '}
          terms v
          {application.signAttestation?.acceptedTermsVersion ?? TERMS_VERSION}
        </Alert>
      )}

      {superseded && application.signAttestation?.acceptedAt && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The application was updated after your last attestation (
          {new Date(application.signAttestation.acceptedAt).toLocaleString()}
          ). Please complete attestation again so your file reflects the latest
          information.
        </Alert>
      )}

      {!readOnly && needsRecording && (
        <Button variant="contained" color="primary" onClick={openFlow}>
          Review & record attestation
        </Button>
      )}

      {!readOnly && current && (
        <Button variant="outlined" sx={{ ml: 1 }} onClick={openFlow}>
          View / re-attest
        </Button>
      )}

      <Dialog
        open={dialogOpen}
        onClose={closeFlow}
        maxWidth="sm"
        fullWidth
        aria-labelledby="loan-attestation-dialog-title"
      >
        <DialogTitle id="loan-attestation-dialog-title">
          Record loan application attestation
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            You are attesting under the terms of use for this product (version{' '}
            <strong>{TERMS_VERSION}</strong>) that the information in this
            application is true and complete to the best of your knowledge, as
            of the time you submit.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            If you change any answers after submitting, your previous
            attestation will no longer apply until you complete this step again.
          </Typography>
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              bgcolor: 'action.hover',
              border: 1,
              borderColor: 'divider',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmed}
                  onChange={(_, c) => setConfirmed(c)}
                  color="primary"
                />
              }
              label="I have reviewed the information above and agree to record this attestation."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeFlow} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void onRecordAttestation()}
            disabled={readOnly || isLoading || !confirmed}
          >
            {isLoading ? 'Recording…' : 'I agree — record attestation'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
